import { Get } from "@/axios/AxiosFunctions";
import { Button } from "@/components/Core/Button";
import { BaseURL } from "@/config/apiUrl";
import { RenderToast } from "@/helper/HelperFunction";
import { saveLoginUserData } from "@/store/auth/authSlice";
import { logo } from "constant/imagePath";
import moment from "moment";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import classes from "./SSOLogin.module.css";

/** Decodes query-string messages (e.g. OAuth error redirects). */
function decodeQueryMessage(raw) {
  if (!raw) return "";
  try {
    return decodeURIComponent(raw.replace(/\+/g, " "));
  } catch {
    return raw;
  }
}

/**
 * OAuth / SSO callback route. Reads `token` to finish login via `users/get-me`,
 * or `error_message` when the IdP redirect indicates failure.
 */
const SSOLogin = () => {
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [flowStatus, setFlowStatus] = useState("idle");

  const token = searchParams.get("token")?.trim();
  const urlErrorMessage = decodeQueryMessage(searchParams.get("error_message"));

  // An unapproved school is not an error: the login page explains it in a banner.
  useEffect(() => {
    if (urlErrorMessage === "school_awaiting_approval") {
      navigate("/login?approval=pending", { replace: true });
    }
  }, [urlErrorMessage, navigate]);

  useEffect(() => {
    if (!token) return undefined;

    const finishLogin = async () => {
      setFlowStatus("loading");
      const meUrl = BaseURL("users/get-me");
      const response = await Get(meUrl, token, false, dispatch);

      if (response !== undefined) {
        dispatch(
          saveLoginUserData({
            data: {
              token,
              user: response?.data?.data,
            },
          }),
        );
        RenderToast("success", "Login successful");
        navigate("/", { replace: true });
        return;
      }

      RenderToast(
        "error",
        "Unable to complete sign-in. Please try again or contact support.",
      );
      setFlowStatus("failed");
    };

    finishLogin();
  }, [token, dispatch, navigate]);

  const showUrlError = Boolean(urlErrorMessage) && !token;
  const showTokenFailure = Boolean(token) && flowStatus === "failed";
  const showLoading = Boolean(token) && flowStatus === "loading";
  const showMissingParams = !token && !urlErrorMessage && flowStatus === "idle";

  return (
    <div className={classes.container}>
      <div className={classes.form_container}>
        <div className={classes.form}>
          <h6>Single sign-on</h6>
          <p>{showLoading && "Completing your sign-in…"}</p>

          {showLoading && (
            <p className={classes.statusText} role="status" aria-live="polite">
              Verifying your session. Please wait.
            </p>
          )}

          {showUrlError && (
            <div
              className={classes.errorBanner}
              role="alert"
              aria-live="assertive"
            >
              {getUserMessageForErrorCode(urlErrorMessage)}
            </div>
          )}

          {showTokenFailure && (
            <div
              className={classes.errorBanner}
              role="alert"
              aria-live="assertive"
            >
              Sign-in could not be completed. Please return to the login page
              and try again.
            </div>
          )}

          {showMissingParams && (
            <p className={classes.statusText}>
              No sign-in information was provided. Use the login page to
              continue.
            </p>
          )}

          {!showLoading && (
            <Button
              className={classes.primaryBtn}
              label="Back to log in"
              variant="tertiary"
              type="button"
              onClick={() => navigate("/login", { replace: true })}
            />
          )}
        </div>
        <p className={classes.end_text}>
          © {moment().year()} Fluency Compass. All Rights Reserved.
        </p>
      </div>
      <div className={classes.rightSec}>
        <div className={classes.contentWrapper}>
          <img src={logo} alt="" />
          <h1>Welcome to Fluency Compass</h1>
          <h6>Navigate Language Learning Together.</h6>
        </div>
      </div>
    </div>
  );
};

export default SSOLogin;

export const SSO_ERROR_MESSAGE_USER_TEXT = {
  access_denied:
    "Sign-in was cancelled. Please try again if you want to continue.",
  missing_authorization_code:
    "Something went wrong during sign-in. Please start again.",
  invalid_or_expired_sso_state:
    "This sign-in session expired or is invalid. Please start sign-in again.",
  sso_token_exchange_failed:
    "We could not complete sign-in with that provider. Please try again.",
  email_not_provided_by_identity_provider:
    "We could not read an email address from your account. Try another sign-in method or contact support.",
  no_account_associated_with_this_email:
    "There is no account associated with this email. Contact your administrator or use a different account.",
  account_inactive:
    "Your account is inactive. Please contact your administrator.",
  school_awaiting_approval:
    "Your school registration is awaiting approval. We will email you once it has been approved.",
  login_not_allowed_for_this_account:
    "This sign-in method is not available for your account.",
};

export function getUserMessageForErrorCode(errorMessageCode) {
  if (errorMessageCode == null || !String(errorMessageCode).trim()) {
    return "Something went wrong. Please try again.";
  }
  const code = String(errorMessageCode).trim();
  return (
    SSO_ERROR_MESSAGE_USER_TEXT[code] ??
    "Sign-in did not complete. Please try again."
  );
}
